import React, { useState, useEffect, useCallback, useMemo } from 'react';
import carnetCommandeApi from '../services/carnet-commande';
import permissionsApi from '../services/permissions';
import { TYPE_ANOMALIE, anomalieIcon, anomalieBadge } from './shared/AnomaliePiece';
import { StockCell } from './shared/StockMagasin';
import './CarnetCommande.css';
import { PlanificationCell, useDossierPlanning } from './shared/DossierPlanification';
import { afficherImmat, immatCorrespond } from '../utils/immat';
import { ExpeditionChip, ExpeditionMarqueur } from './shared/Expedition';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

const STATUT_LIVRAISON = {
  livre:                { label: 'Reçu',         kind: 'success', order: 3 },
  livre_partiellement:  { label: 'Reçu partiel', kind: 'warning', order: 2 },
  recu_partiellement:   { label: 'Reçu partiel', kind: 'warning', order: 2 },
  en_commande:          { label: 'En commande',  kind: 'info',    order: 1 },
};

const STATUT_FACTURATION = {
  facture:                { label: 'Facturé',         kind: 'success', order: 3 },
  facture_partiellement:  { label: 'Facturé partiel', kind: 'warning', order: 2 },
};

function statutBadge(statut) {
  const s = STATUT_LIVRAISON[statut] || STATUT_LIVRAISON.en_commande;
  return <span className={`cc-badge ${s.kind}`}>{s.label}</span>;
}

function facturationBadge(statut) {
  if (!statut) return null;
  const s = STATUT_FACTURATION[statut];
  if (!s) return null;
  return <span className={`cc-badge ${s.kind}`}>{s.label}</span>;
}

// StockCell vit désormais dans `shared/StockMagasin.js` : l'onglet Pièces de la
// modale d'intervention l'affiche aussi, et deux copies auraient divergé.

// La colonne Planification est visible de TOUS ceux qui ont accès à la page
// (2026-09-01) : savoir quand le véhicule est attendu à l'atelier fait partie
// du suivi des pièces, pas de l'administration. Seule l'OUVERTURE du dossier
// reste conditionnée à l'accès au Planning — même règle qu'en Gestion Parking.
function getColumns() {
  return [
    { key: 'num_or',              label: 'OR N°' },
    { key: 'immat',               label: 'Immat.' },
    { key: 'marque',              label: 'Marque' },
    { key: 'receptionnaire',      label: 'Réceptionnaire' },
    { key: 'date_service',        label: 'Date service',     type: 'date' },
    { key: 'nb_lignes',           label: 'Nb pièces',        type: 'number' },
    { key: 'date_planification',  label: 'Planification',    type: 'date' },
    { key: 'statut_livraison_or', label: 'Statut livraison', type: 'statut' },
    { key: 'statut_facturation_or', label: 'Facturation',    type: 'statut_fact' },
  ];
}

function compareRows(a, b, key, type) {
  let va = a[key], vb = b[key];
  if (type === 'number') return (va || 0) - (vb || 0);
  if (type === 'date') return (va || '') < (vb || '') ? -1 : (va || '') > (vb || '') ? 1 : 0;
  if (type === 'statut') {
    return (STATUT_LIVRAISON[va]?.order || 0) - (STATUT_LIVRAISON[vb]?.order || 0);
  }
  if (type === 'statut_fact') {
    return (STATUT_FACTURATION[va]?.order || 0) - (STATUT_FACTURATION[vb]?.order || 0);
  }
  return (va || '').localeCompare(vb || '', 'fr', { sensitivity: 'base' });
}

export default function CarnetCommande({ user, rows: propRows, loading: propLoading, reload, setHeaderAction }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const COLUMNS = useMemo(() => getColumns(), []);

  // La date est affichée pour tous, mais n'est CLIQUABLE que si le compte a
  // accès au Planning : la modale qu'elle ouvre est celle du Planning Atelier
  // et ses appels seraient refusés (403) sans ce droit. Sans accès, la date
  // reste lisible aux couleurs de son statut, inerte — patron GestionParking.
  const [canOpenPlanning, setCanOpenPlanning] = useState(isSuperAdmin);
  useEffect(() => {
    if (isSuperAdmin) return;
    let annule = false;
    permissionsApi.checkAccess('planning')
      .then(r => { if (!annule) setCanOpenPlanning(!!r.data?.hasAccess); })
      .catch(() => {});
    return () => { annule = true; };
  }, [isSuperAdmin]);

  const rows = propRows || [];
  const loading = propLoading ?? false;

  // Colonne Planification : ouverture du dossier sans quitter le carnet.
  // `reload` est le rechargement fourni par la page parente (SuiviPiecesAtelier).
  const { ouvrir: ouvrirDossier, modale: modaleDossier } = useDossierPlanning({
    user, onEnregistre: () => { if (reload) reload(); },
  });

  const [search, setSearch]     = useState('');
  const [syncing, setSyncing]   = useState(false);
  const [syncMsg, setSyncMsg]   = useState(null);
  const [syncErr, setSyncErr]   = useState(null);
  const [modal, setModal]       = useState(null);
  // Société = nom de concession ici : c'est la concession du menu qui filtre (2026-10-26).
  const [filtreSociete] = useState('');
  const [filtreLivraison, setFiltreLivraison] = useState('');
  const [filtreParking, setFiltreParking] = useState(false);
  const [sortKey, setSortKey]   = useState(null);
  const [sortDir, setSortDir]   = useState('asc');

  // Pas de bouton d'action dans le header

  // Anomalie form state
  const [anomalieForm, setAnomalieForm] = useState(null); // { ref, index } or null
  const [anomalieType, setAnomalieType] = useState('endommagee');
  const [anomalieComment, setAnomalieComment] = useState('');
  const [anomalieSaving, setAnomalieSaving] = useState(false);
  // Resolve form state
  const [resolveForm, setResolveForm] = useState(null); // anomalie id or null
  // Correction du commentaire d'une anomalie ouverte. Jusqu'ici il fallait
  // supprimer puis recréer l'anomalie, ce qui lui faisait perdre son ancienneté
  // et sa trace dans les Nouveautés pièces.
  const [editForm, setEditForm] = useState(null);       // anomalie id or null
  const [editComment, setEditComment] = useState('');
  const [resolveComment, setResolveComment] = useState('');

  // Stock magasin par référence pièce (fetché à l'ouverture modale)
  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);

  // Reload data after anomalie action, then update modal
  const reloadAndUpdateModal = useCallback(async (numOr) => {
    if (!reload) return;
    await reload();
  }, [reload]);

  // Update modal when rows change
  useEffect(() => {
    if (modal) {
      const updated = rows.find(r => r.num_or === modal.num_or);
      if (updated) setModal(updated);
    }
  }, [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  // Société affichée : nom de concession mappé (societe_nom) ou fallback code brut.
  const societeLabel = (r) => r.societe_nom || r.societe;

  const filtered = rows.filter(r => {
    const label = societeLabel(r);
    if (filtreSociete && label !== filtreSociete) return false;
    if (!filtreSociete && user?.concession_nom && label !== user.concession_nom) return false;
    if (filtreLivraison && r.statut_livraison_or !== filtreLivraison) return false;
    if (filtreParking && !r.has_parking) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.num_or      || '').toLowerCase().includes(q) ||
      (r.nom_client  || '').toLowerCase().includes(q) ||
      immatCorrespond(r.immat, search) ||
      (r.modele      || '').toLowerCase().includes(q)
    );
  });

  const displayed = useMemo(() => {
    if (!sortKey) return filtered;
    const col = COLUMNS.find(c => c.key === sortKey);
    const sorted = [...filtered].sort((a, b) => compareRows(a, b, sortKey, col?.type));
    return sortDir === 'desc' ? sorted.reverse() : sorted;
  }, [filtered, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    setSyncErr(null);
    try {
      const res = await carnetCommandeApi.sync();
      setSyncMsg(res.data.message || 'Synchronisation réussie');
      if (reload) await reload();
    } catch (err) {
      setSyncErr(err.response?.data?.error || 'Erreur lors de la synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleCreateAnomalie = async () => {
    if (!anomalieForm || !modal) return;
    setAnomalieSaving(true);
    try {
      await carnetCommandeApi.createAnomalie({
        num_or: modal.num_or,
        reference_piece: anomalieForm.ref,
        type_anomalie: anomalieType,
        commentaire: anomalieComment || null,
      });
      setAnomalieForm(null);
      setAnomalieComment('');
      setAnomalieType('endommagee');
      await reloadAndUpdateModal(modal.num_or);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du signalement');
    } finally {
      setAnomalieSaving(false);
    }
  };

  const handleUpdateAnomalie = async (anomalieId) => {
    if (!modal) return;
    setAnomalieSaving(true);
    try {
      await carnetCommandeApi.updateAnomalie(anomalieId, { commentaire: editComment });
      setEditForm(null);
      setEditComment('');
      await reloadAndUpdateModal(modal.num_or);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la modification');
    } finally {
      setAnomalieSaving(false);
    }
  };

  const handleResolveAnomalie = async (anomalieId) => {
    if (!modal) return;
    setAnomalieSaving(true);
    try {
      await carnetCommandeApi.resolveAnomalie(anomalieId, {
        resolution_commentaire: resolveComment || null,
      });
      setResolveForm(null);
      setResolveComment('');
      await reloadAndUpdateModal(modal.num_or);
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la résolution');
    } finally {
      setAnomalieSaving(false);
    }
  };

  // Le détail des pièces n'arrive plus avec la liste (elle ne porte que les
  // lignes en anomalie) : on le charge à l'ouverture, pour cet OR seulement.
  // La modale s'ouvre IMMÉDIATEMENT avec ce qu'on a — l'utilisateur voit
  // l'en-tête pendant que les lignes arrivent, plutôt qu'un écran figé.
  const [modalChargement, setModalChargement] = useState(false);
  const openModal = async (or) => {
    setModal(or);
    setAnomalieForm(null);
    setResolveForm(null);
    if (!or?.num_or) return;
    setModalChargement(true);
    try {
      const { data } = await carnetCommandeApi.getByOr(or.num_or);
      if (data?.lignes) {
        setModal(m => (m && m.num_or === or.num_or ? { ...m, lignes_json: data.lignes } : m));
      }
    } catch {
      // En cas d'échec on garde les lignes en anomalie déjà présentes : la
      // modale reste utilisable, amputée du détail plutôt que vide.
    } finally {
      setModalChargement(false);
    }
  };

  // Fetch stock magasin à l'ouverture de la modale (1 requête, scope = lignes de l'OR).
  useEffect(() => {
    if (!modal || !modal.lignes_json) {
      setStockMap({});
      return;
    }
    const refs = Array.from(new Set(
      modal.lignes_json.map(l => (l.reference || '').trim()).filter(Boolean)
    ));
    if (refs.length === 0) {
      setStockMap({});
      return;
    }
    setStockLoading(true);
    carnetCommandeApi.lookupStock(refs, modal.concession_id)
      .then(res => setStockMap(res.data || {}))
      .catch(() => setStockMap({}))
      .finally(() => setStockLoading(false));
  }, [modal?.num_or, modal?.concession_id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="cc-wrap">
      {/* Messages sync */}
      {syncMsg && <div className="cc-sync-ok">✅ {syncMsg}</div>}
      {syncErr && <div className="cc-sync-err">❌ {syncErr}</div>}

      {/* Recherche + filtres */}
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={`page-select${filtreLivraison ? ' active-filter' : ''}`}
          value={filtreLivraison}
          onChange={e => setFiltreLivraison(e.target.value)}
        >
          <option value="">Tous les statuts</option>
          <option value="en_commande">En commande</option>
          <option value="recu_partiellement">Reçu partiellement</option>
          <option value="livre">Reçu</option>
        </select>
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <button
          className={`cc-btn-parking${filtreParking ? ' active' : ''}`}
          onClick={() => setFiltreParking(v => !v)}
          title="Filtrer les véhicules au parking"
        >
          🅿️
        </button>
        <span className="cc-count">{displayed.length} OR{displayed.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="cc-loading">Chargement…</div>
      ) : displayed.length === 0 ? (
        <div className="cc-empty">Aucune commande.{rows.length === 0 ? ' Cliquez sur "Synchroniser SFTP" pour importer les données.' : ''}</div>
      ) : (
        <div className="table-container">
          <table className="cc-table">
            <thead>
              <tr>
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className="cc-sortable"
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {sortKey === col.key && (
                      <span className="cc-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.map(or => (
                <tr key={or.id} className="cc-row" onClick={() => openModal(or)}>
                  <td className="cc-or">
                    {or.num_or || '—'}
                    <br/><span className="cc-client-sub">{or.nom_client || ''}</span>
                  </td>
                  <td>
                    <div className="cc-immat-cell">
                      <span>{or.immat ? afficherImmat(or.immat) : '—'}</span>
                      {or.has_parking && <span className="cc-badge-parking" title="Présent au parking">🅿️</span>}
                    </div>
                  </td>
                  <td>{or.marque || '—'}</td>
                  <td>{or.receptionnaire || '—'}</td>
                  <td>{formatDate(or.date_service)}</td>
                  <td className="cc-nb">{or.nb_lignes || 0}</td>
                  <td onClick={e => e.stopPropagation()}>
                    <PlanificationCell dossier={or} onOuvrir={ouvrirDossier} cliquable={canOpenPlanning} />
                  </td>
                  <td>
                    {statutBadge(or.statut_livraison_or)}
                    {/* Signal seul : « il y a du suivi transitaire ici ».
                        Le détail se lit dans la fiche, ligne par ligne. */}
                    <ExpeditionMarqueur resume={or.sifa_resume} />
                    {or.nb_anomalies > 0 && (
                      <span className="cc-anomalie-count" title={`${or.nb_anomalies} anomalie${or.nb_anomalies > 1 ? 's' : ''} active${or.nb_anomalies > 1 ? 's' : ''}`}>
                        ⚠️ {or.nb_anomalies}
                      </span>
                    )}
                  </td>
                  <td>{facturationBadge(or.statut_facturation_or)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modale détail pièces */}
      {modal && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 className="cc-modal-title">OR {modal.num_or}</h3>
                <div className="cc-modal-subtitle">
                  {modal.nom_client && <span>{modal.nom_client}</span>}
                  {modal.immat && <span> · {afficherImmat(modal.immat)}</span>}
                  {modal.date_service && <span> · {formatDate(modal.date_service)}</span>}
                  <span style={{ marginLeft: 8 }}>{statutBadge(modal.statut_livraison_or)}</span>
                  {modal.nb_anomalies > 0 && (
                    <span className="cc-modal-anomalie-summary">
                      ⚠️ {modal.nb_anomalies} anomalie{modal.nb_anomalies > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>

            <div className="modal-body">
              {modalChargement && (!modal.lignes_json || modal.lignes_json.length === 0) ? (
                <div className="cc-empty">Chargement des pièces…</div>
              ) : (!modal.lignes_json || modal.lignes_json.length === 0) ? (
                <p className="cc-modal-empty">Aucune ligne de pièce.</p>
              ) : (
                <div className="cc-modal-table-wrap">
                  <table className="cc-modal-table">
                    <colgroup>
                      <col className="cc-col-ref" />
                      <col />
                      <col className="cc-col-qty" />
                      <col className="cc-col-stock" />
                      <col className="cc-col-st" />
                      <col className="cc-col-exp" />
                      <col className="cc-col-fact" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Référence</th>
                        <th>Description<br/><span style={{fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)'}}>(Type cde · N° cde)</span></th>
                        <th className="cc-modal-center">Cdé / Livrée</th>
                        <th>Stock<br/><span style={{fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)'}}>magasin</span></th>
                        <th>Statut<br/><span style={{fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)'}}>(Calculé)</span></th>
                        <th>Transport<br/><span style={{fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)'}}>🚢 transitaire</span></th>
                        <th>Facturation<br/><span style={{fontWeight: 400, fontSize: '0.75rem', color: 'var(--text-muted)'}}>(Calculé)</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {modal.lignes_json.map((l, i) => {
                        const hasActiveAnomalie = !!l.anomalie_active;
                        const isFormOpen = anomalieForm?.index === i;
                        const isResolveOpen = resolveForm === l.anomalie_active?.id;
                        const isEditOpen = editForm === l.anomalie_active?.id;

                        return (
                          <React.Fragment key={i}>
                            <tr className={hasActiveAnomalie ? 'cc-row-anomalie' : ''}>
                              <td className="cc-modal-ref">
                                {l.ref_rempl ? (
                                  <>
                                    <span className="cc-ref-remplacee">{l.reference || '—'}</span>
                                    <br />
                                    <span className="cc-ref-remplacement">{l.ref_rempl}</span>
                                  </>
                                ) : (l.reference || '—')}
                              </td>
                              <td className="cc-modal-desc">
                                <div className="cc-desc-main">
                                  {l.fournisseur && (
                                    <span className="cc-fournisseur">{l.fournisseur}</span>
                                  )}
                                  {l.description || '—'}
                                </div>
                                {(l.type_commande || l.cde) && (
                                  <div className="cc-desc-sub">
                                    <span className="cc-cde-label">CMD</span>
                                    {l.type_commande && <span className="cc-cde-type">{l.type_commande}</span>}
                                    {l.type_commande && l.cde && <span className="cc-cde-sep"> · </span>}
                                    {l.cde && <span className="cc-cde-num">{l.cde}</span>}
                                  </div>
                                )}
                              </td>
                              <td className="cc-modal-center cc-qty-pair">
                                {(() => {
                                  const cde = l.cde_qty ?? 0;
                                  const liv = l.qte_livree ?? 0;
                                  const kind = cde === 0 ? '' : (liv >= cde ? 'full' : (liv > 0 ? 'partial' : 'none'));
                                  return (
                                    <span className={`cc-qty-pair-val ${kind}`}>
                                      {cde}<span className="cc-qty-pair-sep">/</span>{liv}
                                    </span>
                                  );
                                })()}
                              </td>
                              <td>
                                {l.reference
                                  ? <StockCell partRef={l.reference} stockMap={stockMap} loading={stockLoading} />
                                  : <span className="cc-stock-none">—</span>}
                              </td>
                              <td>{statutBadge(l.statut_livraison)}</td>
                              <td>{l.sifa ? <ExpeditionChip sifa={l.sifa} livree={l.statut_livraison === 'livre'} /> : <span className="cc-stock-none">—</span>}</td>
                              <td>{facturationBadge(l.statut_facturation)}</td>
                            </tr>

                            {/* Sous-ligne anomalie : toujours présente, contient
                                soit le bouton "Signaler", soit le badge + résoudre,
                                soit un formulaire (signaler / résoudre + historique). */}
                            <tr className="cc-anomalie-row">
                              <td colSpan={6} className="cc-anomalie-td">
                                {isFormOpen ? (
                                  <div className="cc-anomalie-form">
                                    <span className="cc-anomalie-form-label">Signaler sur <strong>{l.reference}</strong> :</span>
                                    <select
                                      value={anomalieType}
                                      onChange={e => setAnomalieType(e.target.value)}
                                      className="cc-anomalie-select"
                                    >
                                      {Object.entries(TYPE_ANOMALIE).map(([k, v]) => (
                                        <option key={k} value={k}>{v.icon} {v.label}</option>
                                      ))}
                                    </select>
                                    <input
                                      type="text"
                                      className="cc-anomalie-input"
                                      placeholder="Commentaire (optionnel)"
                                      value={anomalieComment}
                                      onChange={e => setAnomalieComment(e.target.value)}
                                    />
                                    <button
                                      className="cc-btn-confirm-signal"
                                      onClick={handleCreateAnomalie}
                                      disabled={anomalieSaving}
                                    >
                                      {anomalieSaving ? '…' : 'Confirmer'}
                                    </button>
                                    <button className="cc-btn-cancel" onClick={() => setAnomalieForm(null)}>Annuler</button>
                                  </div>
                                ) : isEditOpen ? (
                                  <div className="cc-anomalie-form">
                                    <span className="cc-anomalie-form-label">
                                      Commentaire de {anomalieBadge(l.anomalie_active)}
                                    </span>
                                    <input
                                      type="text"
                                      className="cc-anomalie-input"
                                      placeholder="Commentaire"
                                      value={editComment}
                                      autoFocus
                                      onChange={e => setEditComment(e.target.value)}
                                      onKeyDown={e => { if (e.key === 'Enter') handleUpdateAnomalie(l.anomalie_active.id); }}
                                    />
                                    <button
                                      className="cc-btn-confirm-signal"
                                      onClick={() => handleUpdateAnomalie(l.anomalie_active.id)}
                                      disabled={anomalieSaving}
                                    >
                                      {anomalieSaving ? '…' : 'Enregistrer'}
                                    </button>
                                    <button className="cc-btn-cancel" onClick={() => setEditForm(null)}>Annuler</button>
                                  </div>
                                ) : isResolveOpen ? (
                                  <div className="cc-anomalie-resolve-stack">
                                    <div className="cc-anomalie-form">
                                      <span className="cc-anomalie-form-label">
                                        Résoudre {anomalieBadge(l.anomalie_active)}
                                        {l.anomalie_active.commentaire && (
                                          <span className="cc-anomalie-original-comment"> — « {l.anomalie_active.commentaire} »</span>
                                        )}
                                        {l.anomalie_active.created_by_name && (
                                          <span className="cc-anomalie-meta"> (par {l.anomalie_active.created_by_name}, {formatDate(l.anomalie_active.created_at)})</span>
                                        )}
                                      </span>
                                      <input
                                        type="text"
                                        className="cc-anomalie-input"
                                        placeholder="Commentaire de résolution (optionnel)"
                                        value={resolveComment}
                                        onChange={e => setResolveComment(e.target.value)}
                                      />
                                      <button
                                        className="cc-btn-confirm-resolve"
                                        onClick={() => handleResolveAnomalie(l.anomalie_active.id)}
                                        disabled={anomalieSaving}
                                      >
                                        {anomalieSaving ? '…' : 'Résoudre'}
                                      </button>
                                      <button className="cc-btn-cancel" onClick={() => setResolveForm(null)}>Annuler</button>
                                    </div>
                                    {l.anomalies && l.anomalies.filter(a => a.resolved_at).length > 0 && (
                                      <div className="cc-anomalie-history">
                                        <span className="cc-anomalie-history-title">Historique :</span>
                                        {l.anomalies.filter(a => a.resolved_at).map(a => (
                                          <div key={a.id} className="cc-anomalie-history-item">
                                            <span className="cc-anomalie-history-type">{anomalieIcon(a.type_anomalie)} {TYPE_ANOMALIE[a.type_anomalie]?.label}</span>
                                            {a.commentaire && <span> — « {a.commentaire} »</span>}
                                            <span className="cc-anomalie-meta"> Résolu le {formatDate(a.resolved_at)}{a.resolved_by_name ? ` par ${a.resolved_by_name}` : ''}</span>
                                            {a.resolution_commentaire && <span> — « {a.resolution_commentaire} »</span>}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ) : hasActiveAnomalie ? (
                                  <div className="cc-anomalie-active">
                                    {anomalieBadge(l.anomalie_active)}
                                    {l.anomalie_active.commentaire && (
                                      <span className="cc-anomalie-original-comment">« {l.anomalie_active.commentaire} »</span>
                                    )}
                                    {l.anomalie_active.created_by_name && (
                                      <span className="cc-anomalie-meta">par {l.anomalie_active.created_by_name}, {formatDate(l.anomalie_active.created_at)}</span>
                                    )}
                                    <button
                                      className="cc-btn-edit-row"
                                      title="Corriger le commentaire — le type, l'auteur et la date ne changent pas"
                                      onClick={() => { setEditForm(l.anomalie_active.id); setEditComment(l.anomalie_active.commentaire || ''); }}
                                    >
                                      ✏️ Modifier le commentaire
                                    </button>
                                    <button
                                      className="cc-btn-resolve-row"
                                      onClick={() => { setResolveForm(l.anomalie_active.id); setResolveComment(''); }}
                                    >
                                      ✓ Résoudre cette anomalie
                                    </button>
                                  </div>
                                ) : l.reference ? (
                                  <button
                                    className="cc-btn-signal-row"
                                    onClick={() => { setAnomalieForm({ ref: l.reference, index: i }); setAnomalieComment(''); setAnomalieType('endommagee'); }}
                                  >
                                    ⚠️ Signaler une anomalie
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {modaleDossier}
    </div>
  );
}
